import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeftRight, Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import UserSearchSelect from '@/client/components/common/UserSearchSelect';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { hrKeys, useUpsertEmployeeMutation } from '@/client/api/hr/people';
import { useAdminUserConsoleAdminMutation, useAdminUserStaffMutation } from '@/client/api/admin/users.mutation';
import { hasLeft, type Employee } from '@/client/types/hr';
import { Panel } from './hr-ui';

type Pending =
  | { kind: 'console'; grant: boolean }
  | { kind: 'staff'; grant: boolean }
  | { kind: 'relink'; uid: number; label: string }
  | null;

function Pill({ on, onLabel, offLabel }: { on: boolean; onLabel: string; offLabel: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        on ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
      }`}
    >
      {on ? onLabel : offLabel}
    </span>
  );
}

/** The employee's FeelyFeely account, and whether it can use the console.
 *  Every employee has an account; it can be moved to a different one (a
 *  duplicate, a new email) but never removed. Console access and staff status
 *  are granted or revoked here. */
export function AccountAccessPanel({ employee }: { employee: Employee }) {
  const qc = useQueryClient();
  const upsert = useUpsertEmployeeMutation();
  const consoleAdmin = useAdminUserConsoleAdminMutation();
  const staff = useAdminUserStaffMutation();
  const [linking, setLinking] = useState(false);
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState<Pending>(null);
  const [reason, setReason] = useState('');
  const account = employee.account;
  const busy = upsert.isPending || consoleAdmin.isPending || staff.isPending;

  const refresh = () => void qc.invalidateQueries({ queryKey: hrKeys.all });
  const failed = (err: unknown) => toast.error((err as Error).message || 'Something went wrong');

  const run = () => {
    if (!pending) return;
    const why = reason.trim() || undefined;
    const done = (msg: string) => () => {
      toast.success(msg);
      setPending(null);
      setReason('');
      refresh();
    };
    if (pending.kind === 'relink') {
      upsert.mutate(
        { id: employee.id, uid: pending.uid },
        {
          onSuccess: () => {
            done('Account changed')();
            setLinking(false);
            setQuery('');
          },
          onError: failed,
        },
      );
    } else if (pending.kind === 'console' && account) {
      consoleAdmin.mutate(
        { uid: account.uid, admin: pending.grant, reason: why },
        { onSuccess: done(pending.grant ? 'Console access granted' : 'Console access revoked'), onError: failed },
      );
    } else if (pending.kind === 'staff' && account) {
      staff.mutate(
        { uid: account.uid, staff: pending.grant, reason: why },
        { onSuccess: done(pending.grant ? 'Marked as staff' : 'Staff status removed'), onError: failed },
      );
    }
  };

  const confirmCopy: Record<string, { title: string; body: string; action: string; danger?: boolean }> = {
    'console:true': {
      title: 'Grant console access?',
      body: `${employee.name} will be able to sign in to the admin console with ${account?.email || 'their email'}, and will be marked as staff. What they can do there comes from their roles.`,
      action: 'Grant',
    },
    'console:false': {
      title: 'Revoke console access?',
      body: `${employee.name} will no longer be able to sign in to the admin console. Their roles and staff status stay as they are.`,
      action: 'Revoke',
      danger: true,
    },
    'staff:true': {
      title: 'Mark as staff?',
      body: 'Opens staff-only surfaces on the platform (developer tools, test mode). It does not grant console access.',
      action: 'Mark as staff',
    },
    'staff:false': {
      title: 'Remove staff status?',
      body: 'Closes every staff-only surface for this account. Console access must be revoked first.',
      action: 'Remove',
      danger: true,
    },
  };
  const copy = !pending
    ? null
    : pending.kind === 'relink'
      ? {
          title: employee.uid == null ? 'Link this account?' : 'Change the linked account?',
          body:
            employee.uid == null
              ? `${employee.name} will be linked to ${pending.label}. Console access and staff status are not changed.`
              : `${employee.name} will be linked to ${pending.label} instead. My workplace follows the new account. Console access and staff status on either account are not changed.`,
          action: employee.uid == null ? 'Link account' : 'Change account',
        }
      : confirmCopy[`${pending.kind}:${pending.grant}`];

  return (
    <Panel title="FeelyFeely account & access">
      {linking && (
        <div className="mb-4 space-y-2 rounded-lg border border-gray-100 p-3">
          <p className="text-xs text-gray-500">Pick the account this employee should be linked to.</p>
          <UserSearchSelect
            value={query}
            onChange={setQuery}
            onSelect={(u) =>
              setPending({ kind: 'relink', uid: u.uid, label: u.email || `${u.first_name} ${u.last_name}`.trim() || `uid ${u.uid}` })
            }
          />
          <button type="button" className="text-xs text-gray-500 hover:underline" onClick={() => setLinking(false)}>
            Cancel
          </button>
        </div>
      )}
      {!account ? (
        <div className="space-y-2">
          <p className="text-sm text-amber-700">
            {employee.uid == null
              ? 'Pending account: no FeelyFeely account is linked yet. Once they sign up, link it here.'
              : `Linked to uid ${employee.uid}, but that account could not be loaded. It may have been deleted. Link the right account.`}
          </p>
          {!linking && (
            <PermissionButton code={PERMISSIONS.hr.manage} variant="outline" size="sm" className="gap-1.5" onClick={() => setLinking(true)}>
              <ArrowLeftRight className="h-4 w-4" /> {employee.uid == null ? 'Link account' : 'Change account'}
            </PermissionButton>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900">{account.name || account.email}</p>
              <p className="truncate text-xs text-gray-500">
                {account.email || 'No email'} · uid {account.uid}
                {!account.active && ' · account inactive'}
              </p>
              <Link to={`/admin/users/${account.uid}`} className="text-xs font-medium text-blue-700 hover:underline">
                Roles and sessions
              </Link>
            </div>
            <PermissionButton
              code={PERMISSIONS.hr.manage}
              variant="ghost"
              size="sm"
              className="gap-1.5 text-gray-500"
              onClick={() => setLinking(true)}
            >
              <ArrowLeftRight className="h-4 w-4" /> Change account
            </PermissionButton>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-100 p-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-900">Console access</span>
                <Pill on={account.consoleAccess} onLabel="Can sign in" offLabel="No access" />
              </div>
              {hasLeft(employee) && account.consoleAccess && (
                <p className="mt-1 text-xs text-red-700">Has left but can still sign in. Revoke this as part of offboarding.</p>
              )}
            </div>
            <PermissionButton
              code={PERMISSIONS.system.consoleAdmin}
              variant="outline"
              size="sm"
              disabled={busy}
              className={account.consoleAccess ? 'border-red-200 text-red-600 hover:bg-red-50' : undefined}
              onClick={() => setPending({ kind: 'console', grant: !account.consoleAccess })}
            >
              {account.consoleAccess ? 'Revoke' : 'Grant'}
            </PermissionButton>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-100 p-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-900">Staff status</span>
              <Pill on={account.staff} onLabel="Staff" offLabel="Not staff" />
            </div>
            <PermissionButton
              code={PERMISSIONS.system.employees}
              variant="outline"
              size="sm"
              disabled={busy || (account.staff && account.consoleAccess)}
              title={account.staff && account.consoleAccess ? 'Revoke console access first' : undefined}
              onClick={() => setPending({ kind: 'staff', grant: !account.staff })}
            >
              {account.staff ? 'Remove' : 'Mark as staff'}
            </PermissionButton>
          </div>

          {account.roles.length > 0 && (
            <p className="text-xs text-gray-500">Roles: {account.roles.join(', ')}</p>
          )}
        </div>
      )}

      <AlertDialog open={pending !== null} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
            <AlertDialogDescription>{copy?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          {pending && pending.kind !== 'relink' && (
            <div className="space-y-1 py-1">
              <Label htmlFor={`hr-access-reason-${employee.id}`}>Reason (optional)</Label>
              <Input
                id={`hr-access-reason-${employee.id}`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Recorded in the audit log"
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                run();
              }}
              disabled={busy}
              className={copy?.danger ? 'bg-red-600 hover:bg-red-700' : undefined}
            >
              {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {copy?.action}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
}
